import { useId, useState } from 'react';
import {
  MiceCorrectEmailRequestSchema,
  isEmailFixable,
  type MiceOrderError,
} from '@voll-intelligence/domain';
import { t } from '@web/i18n';
import { Button, Input } from '@web/ui/core';

interface EmailCorrectionProps {
  row: { status: string; error: MiceOrderError | null; travelerEmail: string };
  canPurchase: boolean;
  busy: boolean;
  /** `onDone` fecha o campo quando o servidor aceita. */
  onCorrect: (email: string, onDone: () => void) => void;
}

/** O e-mail como o servidor o grava (trim + minúsculas); `null` se ele recusaria. */
const parsedEmail = (raw: string): string | null => {
  const parsed = MiceCorrectEmailRequestSchema.safeParse({ email: raw });
  return parsed.success ? parsed.data.email : null;
};

/**
 * "Corrigir e-mail" na linha importada que ficou sem viajante (não encontrado ou de fora do grupo):
 * o servidor a devolve à validação com o e-mail novo. O mesmo e-mail é o pedido de buscar o viajante
 * de novo (cadastrado depois da importação). Quem monta passa `key` com o id da linha.
 */
export function EmailCorrection({
  row,
  canPurchase,
  busy,
  onCorrect,
}: Readonly<EmailCorrectionProps>): React.JSX.Element | null {
  const [email, setEmail] = useState<string | null>(null);
  const reasonId = useId();
  if (!canPurchase || !isEmailFixable(row)) return null;
  if (email === null) {
    return (
      <div>
        <Button
          size="xs"
          variant="outline"
          onClick={() => {
            setEmail(row.travelerEmail);
          }}
        >
          {t('mice.emailFix.open')}
        </Button>
      </div>
    );
  }
  const next = parsedEmail(email);
  const same = next !== null && next === parsedEmail(row.travelerEmail);
  return (
    <form
      className="flex flex-col gap-1.5"
      onSubmit={(e) => {
        e.preventDefault();
        if (next === null) return;
        onCorrect(next, () => {
          setEmail(null);
        });
      }}
    >
      <div className="flex items-center gap-2">
        <Input
          type="email"
          aria-label={t('mice.emailFix.field')}
          value={email}
          aria-invalid={next === null}
          aria-describedby={next === null ? reasonId : undefined}
          onChange={(e) => {
            setEmail(e.target.value);
          }}
        />
        <Button size="sm" type="submit" disabled={busy || next === null}>
          {t(same ? 'mice.emailFix.retry' : 'mice.emailFix.submit')}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          type="button"
          onClick={() => {
            setEmail(null);
          }}
        >
          {t('mice.emailFix.cancel')}
        </Button>
      </div>
      {next === null ? (
        <p id={reasonId} className="text-micro text-destructive">
          {t('mice.emailFix.incomplete')}
        </p>
      ) : null}
      <p className="text-micro text-muted-foreground">{t('mice.emailFix.hint')}</p>
    </form>
  );
}
